/**
 * Team keychain UI (task #52).
 *
 * Reusable in two places:
 * - Team settings dialog (all entries, client assignment shown/selectable)
 * - Project detail dialog (filtered to the project's client, new entries
 *   pre-assigned to that client)
 *
 * Lock model mirrors the server: the list (titles/links/usernames) is always
 * visible to authenticated users, but revealing/copying passwords and
 * editing/deleting entries requires a fresh password-or-passkey confirmation
 * ("unlock") that expires server-side after a few minutes. Adding an entry
 * never requires the unlock.
 */
import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { startAuthentication } from "@simplewebauthn/browser";
import { apiRequest } from "@/lib/queryClient";
import { authedFetch } from "@/lib/authed-fetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useClients } from "@/hooks/use-shared-queries";
import {
  KeyRound,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ExternalLink,
  Fingerprint,
} from "lucide-react";
import type { KeychainEntrySafe } from "@shared/schema";

interface KeychainStatus {
  unlocked: boolean;
  expiresAt: number | null;
}

interface KeychainSectionProps {
  /** When set, only entries of this client are listed and new entries are pre-assigned to it. */
  clientId?: string;
}

interface EntryFormState {
  title: string;
  url: string;
  username: string;
  password: string;
  clientId: string; // "" = unassigned
}

const EMPTY_FORM: EntryFormState = { title: "", url: "", username: "", password: "", clientId: "" };

export function KeychainSection({ clientId }: KeychainSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: clients } = useClients();

  const entriesKey = clientId
    ? [`/api/keychain/entries?clientId=${clientId}`]
    : ["/api/keychain/entries"];

  const { data: status } = useQuery<KeychainStatus>({
    queryKey: ["/api/keychain/status"],
    refetchInterval: 30_000,
  });
  const unlocked = !!status?.unlocked;

  // Re-lock the UI automatically when the server-side grant expires.
  useEffect(() => {
    if (!status?.unlocked || !status.expiresAt) return;
    const ms = status.expiresAt - Date.now();
    if (ms <= 0) return;
    const t = setTimeout(() => {
      setRevealed({});
      queryClient.invalidateQueries({ queryKey: ["/api/keychain/status"] });
    }, ms + 500);
    return () => clearTimeout(t);
  }, [status?.unlocked, status?.expiresAt, queryClient]);

  const { data: entries = [], isLoading } = useQuery<KeychainEntrySafe[]>({
    queryKey: entriesKey,
  });

  // ----- unlock -----
  const [password, setPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const afterUnlock = useCallback(() => {
    setPassword("");
    setUnlockError("");
    queryClient.invalidateQueries({ queryKey: ["/api/keychain/status"] });
  }, [queryClient]);

  const unlockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/keychain/unlock", {
        method: "password",
        password,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Confirmation failed");
      }
      return res.json();
    },
    onSuccess: afterUnlock,
    onError: (e: Error) => setUnlockError(e.message),
  });

  const handlePasskeyUnlock = async () => {
    setUnlockError("");
    setPasskeyLoading(true);
    try {
      const optionsRes = await apiRequest("POST", "/api/keychain/unlock-options", {});
      if (!optionsRes.ok) throw new Error("Failed to get passkey options");
      const { challengeToken, ...optionsJSON } = await optionsRes.json();
      const authResp = await startAuthentication({ optionsJSON });
      const verifyRes = await apiRequest("POST", "/api/keychain/unlock", {
        method: "passkey",
        response: authResp,
        challengeToken,
      });
      if (!verifyRes.ok) {
        const data = await verifyRes.json().catch(() => ({}));
        throw new Error(data.error || "Passkey confirmation failed");
      }
      afterUnlock();
    } catch (err: any) {
      if (err?.name === "NotAllowedError") {
        setUnlockError("Passkey confirmation was cancelled");
      } else {
        setUnlockError(err?.message || "Passkey confirmation failed");
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // ----- reveal / copy -----
  const [revealed, setRevealed] = useState<Record<number, string>>({});

  const fetchPassword = useCallback(async (entryId: number): Promise<string> => {
    const res = await authedFetch(`/api/keychain/entries/${entryId}/reveal`, {
      credentials: "include",
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || "Failed to reveal password");
    }
    const data = await res.json();
    return data.password as string;
  }, []);

  const handleReveal = async (entryId: number) => {
    if (revealed[entryId] !== undefined) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
      return;
    }
    try {
      const pw = await fetchPassword(entryId);
      setRevealed((prev) => ({ ...prev, [entryId]: pw }));
    } catch (e: any) {
      toast({ title: e.message || "Failed to reveal password", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/keychain/status"] });
    }
  };

  const handleCopyPassword = async (entryId: number) => {
    try {
      const pw = revealed[entryId] ?? (await fetchPassword(entryId));
      await navigator.clipboard.writeText(pw);
      toast({ title: "Password copied" });
    } catch (e: any) {
      toast({ title: e.message || "Failed to copy password", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/keychain/status"] });
    }
  };

  const handleCopyUsername = async (username: string) => {
    try {
      await navigator.clipboard.writeText(username);
      toast({ title: "Username copied" });
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  // ----- add / edit / delete -----
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<EntryFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<KeychainEntrySafe | null>(null);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, clientId: clientId || "" });
    setShowForm(true);
  };

  const openEdit = (entry: KeychainEntrySafe) => {
    setEditingId(entry.id);
    setForm({
      title: entry.title,
      url: entry.url || "",
      username: entry.username || "",
      password: "", // left blank = keep current password
      clientId: entry.clientId || "",
    });
    setShowForm(true);
  };

  const invalidateEntries = () => {
    queryClient.invalidateQueries({
      predicate: (q) =>
        typeof q.queryKey[0] === "string" &&
        (q.queryKey[0] as string).startsWith("/api/keychain/entries"),
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, any> = {
        title: form.title.trim(),
        url: form.url.trim() || null,
        username: form.username.trim() || null,
        clientId: form.clientId || null,
      };
      if (editingId === null) {
        payload.password = form.password;
        const res = await apiRequest("POST", "/api/keychain/entries", payload);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to create entry");
        }
      } else {
        if (form.password) payload.password = form.password;
        const res = await apiRequest("PATCH", `/api/keychain/entries/${editingId}`, payload);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to update entry");
        }
      }
    },
    onSuccess: () => {
      invalidateEntries();
      setShowForm(false);
      setRevealed({});
      toast({ title: editingId === null ? "Keychain entry added" : "Keychain entry updated" });
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/keychain/entries/${id}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete entry");
      }
    },
    onSuccess: () => {
      invalidateEntries();
      setDeleteTarget(null);
      toast({ title: "Keychain entry deleted" });
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const clientName = (id: string | null) =>
    (id && clients?.find((c) => c.id === id)?.companyName) || null;

  const canSave =
    form.title.trim().length > 0 && (editingId !== null || form.password.length > 0);

  return (
    <div className="space-y-3" data-testid="keychain-section">
      {/* Lock banner / unlock prompt */}
      {unlocked ? (
        <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-950/30 border border-emerald-800/50 rounded px-3 py-2">
          <Unlock className="h-3.5 w-3.5" />
          <span>
            Keychain unlocked — passwords can be revealed, edited, and deleted until it
            re-locks automatically.
          </span>
        </div>
      ) : (
        <div className="border border-gray-600 rounded p-3 space-y-2 bg-gray-800/50">
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <Lock className="h-4 w-4 text-amber-400" />
            <span>Confirm your identity to view or change passwords.</span>
          </div>
          <form
            className="flex flex-col sm:flex-row gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (password) unlockMutation.mutate();
            }}
          >
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="Your login password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-8 text-sm"
              data-testid="input-keychain-password"
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                className="h-8"
                disabled={!password || unlockMutation.isPending}
                data-testid="button-keychain-unlock"
              >
                {unlockMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Unlock className="h-3.5 w-3.5 mr-1" />
                )}
                Unlock
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8"
                onClick={handlePasskeyUnlock}
                disabled={passkeyLoading}
                data-testid="button-keychain-passkey"
              >
                {passkeyLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Fingerprint className="h-3.5 w-3.5 mr-1" />
                )}
                Use passkey
              </Button>
            </div>
          </form>
          {unlockError && <p className="text-xs text-red-400">{unlockError}</p>}
        </div>
      )}

      {/* Entry list */}
      {isLoading ? (
        <div className="text-xs text-gray-400 py-4 text-center flex items-center justify-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-gray-400 py-3 text-center">
          No keychain entries{clientId ? " for this client" : ""} yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="border border-gray-600 rounded px-3 py-2 bg-gray-800/40"
              data-testid={`keychain-entry-${entry.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <KeyRound className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-200 truncate">
                      {entry.title}
                    </span>
                    {!clientId && clientName(entry.clientId) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-950/60 border border-blue-800/60 text-blue-300">
                        {clientName(entry.clientId)}
                      </span>
                    )}
                  </div>
                  {entry.url && (
                    <a
                      href={/^https?:\/\//.test(entry.url) ? entry.url : `https://${entry.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-400 hover:text-blue-300 underline inline-flex items-center gap-1 mt-0.5 break-all"
                    >
                      {entry.url}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  )}
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {entry.username && (
                      <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                        {entry.username}
                        <button
                          onClick={() => handleCopyUsername(entry.username!)}
                          className="text-gray-500 hover:text-gray-200"
                          title="Copy username"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    <span className="text-xs font-mono text-gray-400 inline-flex items-center gap-1.5">
                      {revealed[entry.id] !== undefined ? revealed[entry.id] : "••••••••"}
                      <button
                        onClick={() => handleReveal(entry.id)}
                        disabled={!unlocked}
                        className="text-gray-500 hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={unlocked ? "Reveal password" : "Unlock the keychain first"}
                        data-testid={`button-reveal-${entry.id}`}
                      >
                        {revealed[entry.id] !== undefined ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                      </button>
                      <button
                        onClick={() => handleCopyPassword(entry.id)}
                        disabled={!unlocked}
                        className="text-gray-500 hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={unlocked ? "Copy password" : "Unlock the keychain first"}
                        data-testid={`button-copy-password-${entry.id}`}
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEdit(entry)}
                    disabled={!unlocked}
                    className="p-1 text-gray-500 hover:text-blue-400 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={unlocked ? "Edit entry" : "Unlock the keychain first"}
                    data-testid={`button-edit-${entry.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(entry)}
                    disabled={!unlocked}
                    className="p-1 text-gray-500 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={unlocked ? "Delete entry" : "Unlock the keychain first"}
                    data-testid={`button-delete-${entry.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Adding never requires the unlock */}
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs w-full border-dashed border-gray-600 text-gray-400 hover:text-gray-200"
        onClick={openAdd}
        data-testid="button-add-keychain-entry"
      >
        <Plus className="h-3 w-3 mr-1" />
        Add Keychain Entry
      </Button>

      {/* Add / edit dialog */}
      <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId === null ? "Add Keychain Entry" : "Edit Keychain Entry"}
            </DialogTitle>
            <DialogDescription>
              Passwords are encrypted before they are stored.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Vimeo team account"
                data-testid="input-entry-title"
              />
            </div>
            <div>
              <Label className="text-xs">Web link</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://…"
                data-testid="input-entry-url"
              />
            </div>
            <div>
              <Label className="text-xs">Username</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                autoComplete="off"
                data-testid="input-entry-username"
              />
            </div>
            <div>
              <Label className="text-xs">
                Password {editingId === null ? "*" : "(leave blank to keep current)"}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
                data-testid="input-entry-password"
              />
            </div>
            <div>
              <Label className="text-xs">Client</Label>
              <Select
                value={form.clientId || "__none__"}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, clientId: v === "__none__" ? "" : v }))
                }
              >
                <SelectTrigger data-testid="select-entry-client">
                  <SelectValue placeholder="No client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No client</SelectItem>
                  {clients?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.id} — {c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!canSave || saveMutation.isPending}
              data-testid="button-save-entry"
            >
              {saveMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              {editingId === null ? "Add Entry" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete keychain entry?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" and its stored password will be permanently removed.
              Admins are notified of every deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
